"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeConfig = exports.createProviderMachine = exports.machineJSON = void 0;
const xstate_1 = require("xstate");
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const States_1 = require("../../lib/constants/States");
const utils_1 = require("../../lib/utils");
const Actions_1 = require("../../lib/constants/Actions");
const api_1 = require("../../lib/api");
const gx_1 = require("../../lib/constants/gx");
const covidVaccineUtils_1 = require("../../lib/covidVaccineUtils");
const actions = {
    setResults: (0, xstate_1.assign)({
        provider: (_ctx, ev) => ev.data.providerData.provider,
        locations: (_ctx, ev) => ev.data.providerData.locations,
        insuranceOptions: (_ctx, ev) => ev.data.insuranceOptions,
        gx: (_ctx, ev) => ev.data.gx
    }),
    setEhrPatientDemographics: (0, xstate_1.assign)({
        ehrPatientList: (_ctx, ev) => {
            // ev.data.ehrPatientProxyDemographics is only present if show_dropdown and
            // show_related are true
            const mainPatientDemographics = ev.data.ehrPatientDemographics;
            const proxyPatientsDemographics = ev.data.ehrPatientProxyDemographics;
            let patientList = [];
            if (mainPatientDemographics) {
                // Create entry for main patient to be first in list
                patientList.push({
                    name: (0, utils_1.makeNameString)(mainPatientDemographics.name),
                    patient_ids: mainPatientDemographics.patientIds,
                    // demographics get populated per use, since the main call
                    // returns a full set of demographics, we set there on the main patient
                    // since we already have them. The proxy patients' demographics may be
                    // incomplete so we need to make a call to fetch those when they are selected
                    demographics: mainPatientDemographics
                });
            }
            if (proxyPatientsDemographics && proxyPatientsDemographics.length > 0) {
                patientList = [...patientList, ...proxyPatientsDemographics];
            }
            return patientList;
        }
    }),
    sendParentNext: (0, xstate_1.sendParent)((ctx) => ({
        type: Actions_1.NEXT,
        data: {
            provider: ctx.provider,
            insuranceOptions: ctx.insuranceOptions,
            locations: ctx.locations,
            gx: ctx.gx,
            ehrPatientList: ctx.ehrPatientList,
            selectedPatientIndex: ctx.ehrPatientList && ctx.ehrPatientList.length > 0 ? 0 : null,
            purpose: ctx.purpose
        }
    })),
    /** This is useful for deepLinking since the purpose param does not contain a visit_method
     * Setting it allows the check of certain conditional GX questions
     * (e.g. the telehealth attestation question which can be triggered by a "video" visit_method)
     */
    setApptPurposeVisitMethod: (0, xstate_1.assign)({
        purpose: (ctx) => {
            var _a, _b, _c;
            const visitMethod = (_c = (_b = (_a = ctx.provider.appointment_ehr_purposes.find(({ name, patient_relationship }) => {
                return (name === ctx.deepLinkingParams.purpose &&
                    patient_relationship === ctx.deepLinkingParams.patientRel);
            })) === null || _a === void 0 ? void 0 : _a.ehr_data) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.visit_method;
            return Object.assign(Object.assign({}, ctx.purpose), { visit_method: visitMethod !== null && visitMethod !== void 0 ? visitMethod : null });
        }
    }),
    /** Setting the context from which the GX backend will read and respond with relevant GX questons
     * - `current_section: 'conditional'` - a flag that notifies the GX backend we want conditional GX questions (vs non-conditional questions)
     * - `visit_method` - this is passed today as the only condition the GX backend checks on whether to return conditional GX questions (e.g. telehealth attestation feature)
     * NOTE: In the future, we may want to pass in more context (such as patient relationship) which could trigger different conditional GX questions
     */
    setConditionalGxContext: (0, xstate_1.assign)({
        gx: (ctx) => {
            var _a, _b, _c;
            const gxVisitMethod = (_b = (_a = ctx.purpose) === null || _a === void 0 ? void 0 : _a.visit_method) !== null && _b !== void 0 ? _b : null;
            const conditionalGXContext = {
                current_section: gx_1.GX_CONTEXT.CURRENT_SECTION.CONDITIONAL,
                visit_method: gxVisitMethod
            };
            const contextualInfo = {
                context: Object.assign(Object.assign({}, (_c = ctx.gx.contextualInfo) === null || _c === void 0 ? void 0 : _c.context), conditionalGXContext)
            };
            return Object.assign(Object.assign({}, ctx.gx), { contextualInfo });
        }
    }),
    /**
     * apptInfo expects this to be cleared upon apptInfo initialization;
     * apptInfo controls GX context internally because it may also call the GX API for conditional questions
     */
    clearConditionalGxContext: (0, xstate_1.assign)({
        gx: (ctx) => {
            const { context: gxContext } = ctx.gx.contextualInfo;
            const filterGxConditionalContextFields = (updatedGxContext, key) => {
                if (!gx_1.GX_CONTEXT_CONDITIONAL_KEYS.includes(key)) {
                    return Object.assign(Object.assign({}, updatedGxContext), { [key]: gxContext[key] });
                }
            };
            const filterOutConditionalGxContext = Object.keys(gxContext).reduce(filterGxConditionalContextFields, {});
            const contextualInfo = {
                context: filterOutConditionalGxContext
            };
            return Object.assign(Object.assign({}, ctx.gx), { contextualInfo });
        }
    }),
    setConditionalGxQuestions: (0, xstate_1.assign)({
        gx: (ctx, ev) => {
            var _a;
            return (Object.assign(Object.assign({}, ctx.gx), { v2Conditionals: (_a = ev.data.gx) === null || _a === void 0 ? void 0 : _a.v2Conditionals }));
        }
    }),
    /** set the flag used to check if a call was made to GX API to check for conditional questions using relevant and up to date contextualInfo */
    setConditionalContextualInfoSent: (0, xstate_1.assign)({
        gx: (ctx) => (Object.assign(Object.assign({}, ctx.gx), { conditionalContextualInfoSent: true }))
    })
};
const guards = {
    hasNoProviderResults: (_ctx, ev) => {
        const { providerData } = ev.data;
        return !providerData || !providerData.provider || !providerData.provider.id;
    },
    shouldGetEhrPatientDemographics: (ctx) => {
        // Check if the app was initialized with ehr data
        const { pid, pid_type } = ctx.ehr ? ctx.ehr : { pid: null, pid_type: null };
        return !!pid && !!pid_type;
    },
    hasDeepLinkedPurposeAndShouldGetEhrPatientDemographics: (ctx) => {
        const hasDeepLinkedPurpose = !(0, isEmpty_1.default)(ctx.deepLinkingParams.purpose);
        // Check if the app was initialized with ehr data
        const { pid, pid_type } = ctx.ehr ? ctx.ehr : { pid: null, pid_type: null };
        return hasDeepLinkedPurpose && !!pid && !!pid_type;
    },
    hasDeepLinkedPurpose: (ctx) => !(0, isEmpty_1.default)(ctx.deepLinkingParams.purpose),
    hasNoConditionalGxQuestions: (ctx, ev) => {
        var _a;
        // gx api call returns 404 if no conditionals are found
        return !((_a = ev.data.gx) === null || _a === void 0 ? void 0 : _a.v2Conditionals);
    },
    isBookingDisabled: (ctx, ev) => {
        const { providerData } = ev.data;
        return (ctx.dbEnabledProperty && !providerData.provider[ctx.dbEnabledProperty]);
    }
};
const services = {
    getData: (ctx) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const { covidVaccinePatientRegFormConfig, patientRegFormConfig, customerCode, epicOSWConfig, gx, productName, providerId, services, useProviderInsuranceOnRegForm, visibility, apiBaseURL } = ctx;
        const api = new api_1.Api({ customerCode, productName, apiBaseURL });
        const [providerData, gxV2] = yield Promise.all([
            api.constructProviderObject(services.getProviderById, providerId, epicOSWConfig, visibility),
            api.getGxStepByProviderId(providerId, gx.contextualInfo)
        ]);
        let gxV1 = null;
        const providerType = (_a = providerData === null || providerData === void 0 ? void 0 : providerData.provider) === null || _a === void 0 ? void 0 : _a.provider_type;
        if (providerType) {
            gxV1 = yield api.getGxFlowForProviderType(providerType);
        }
        // if we're in the COVID flow and we shouldn't get the insurance plan names
        // for the patient reg form
        let insuranceOptions;
        if ((0, covidVaccineUtils_1.isCovidVaccineProvider)(providerData.provider) &&
            !(0, covidVaccineUtils_1.shouldLoadXpPlanNames)(covidVaccinePatientRegFormConfig || patientRegFormConfig)) {
            // insurance options empty
            insuranceOptions = [];
        }
        else if (!useProviderInsuranceOnRegForm) {
            // fetch the insurance plan names for the patient reg form from XP service
            insuranceOptions = yield api.getXpPlanNames();
        }
        else {
            // get insurance plans from the provider object, sorted
            insuranceOptions = [
                ...(providerData.provider.insurance_accepted || [])
            ].sort();
        }
        return {
            providerData,
            insuranceOptions,
            gx: {
                v1: gxV1,
                v2: gxV2,
                v2Conditionals: null
            }
        };
    }),
    getV2ConditionalsAndGetEhrPatientDemographics: (ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const { ehr, services, customerCode, productName, apiBaseURL } = ctx;
        const { show_dropdown = false, show_related = false } = ehr, ehrData = __rest(ehr, ["show_dropdown", "show_related"]);
        const api = new api_1.Api({ customerCode, productName, apiBaseURL });
        const [v2Conditionals, ehrPatientDemographics, ehrPatientProxyDemographics] = yield Promise.all([
            api.getGxStepByProviderId(ctx.providerId, ctx.gx.contextualInfo),
            api.getEhrPatientDemographics(services.getEhrPatientDemographics, ehrData),
            show_dropdown && show_related
                ? api.getEhrPatientProxyDemographics(services.getEhrPatientProxyDemographics, ehrData)
                : null
        ]);
        // if we fetched the proxy demographics, but not the main patient demographics,
        // we want to pre-fetch the demographics of the first person in the proxy list
        if ((ehrPatientProxyDemographics === null || ehrPatientProxyDemographics === void 0 ? void 0 : ehrPatientProxyDemographics.length) > 0 && !ehrPatientDemographics) {
            const firstProxyDemographics = yield api.getEhrPatientDemographics(services.getEhrPatientDemographics, ehrPatientProxyDemographics[0].patient_ids[0]);
            ehrPatientProxyDemographics[0].demographics = firstProxyDemographics;
        }
        return {
            gx: Object.assign(Object.assign({}, ctx.gx), { v2Conditionals }),
            ehrPatientDemographics,
            ehrPatientProxyDemographics
        };
    }),
    getEhrPatientDemographics: (ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const { ehr, services, customerCode, productName, apiBaseURL } = ctx;
        const { show_dropdown = false, show_related = false } = ehr, ehrData = __rest(ehr, ["show_dropdown", "show_related"]);
        const api = new api_1.Api({ customerCode, productName, apiBaseURL });
        const [ehrPatientDemographics, ehrPatientProxyDemographics] = yield Promise.all([
            api.getEhrPatientDemographics(services.getEhrPatientDemographics, ehrData),
            show_dropdown && show_related
                ? api.getEhrPatientProxyDemographics(services.getEhrPatientProxyDemographics, ehrData)
                : null
        ]);
        // if we fetched the proxy demographics, but not the main patient demographics,
        // we want to pre-fetch the demographics of the first person in the proxy list
        if ((ehrPatientProxyDemographics === null || ehrPatientProxyDemographics === void 0 ? void 0 : ehrPatientProxyDemographics.length) > 0 && !ehrPatientDemographics) {
            const firstProxyDemographics = yield api.getEhrPatientDemographics(services.getEhrPatientDemographics, ehrPatientProxyDemographics[0].patient_ids[0]);
            ehrPatientProxyDemographics[0].demographics = firstProxyDemographics;
        }
        return {
            ehrPatientDemographics,
            ehrPatientProxyDemographics
        };
    }),
    getV2Conditionals: (ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const { customerCode, apiBaseURL, productName } = ctx;
        const api = new api_1.Api({ customerCode, productName, apiBaseURL });
        const v2Conditionals = yield api.getGxStepByProviderId(ctx.providerId, ctx.gx.contextualInfo);
        return {
            gx: Object.assign(Object.assign({}, ctx.gx), { v2Conditionals })
        };
    })
};
/**
 * @typedef {Object} ProviderMachineProps Properties to build the context on the state machine.
 * @property {number} props.providerId
 * @property {Object} props.epicOSWConfig
 * @property {Object} props.covidVaccinePatientRegFormConfig
 * @property {Object} props.services
 * @property {Object} props.ehr
 * @property {Object} props.ehrPatientList
 * @property {string} props.customerCode
 * @property {Object} props.gx
 */
/**
 * @param {ProviderMachineProps} props
 * @returns {Object} state machine config
 * @note This is exposed for snapshot testing
 */
const machineJSON = ({ covidVaccinePatientRegFormConfig, patientRegFormConfig, customerCode, dbEnabledProperty, deepLinkingParams, ehr, ehrPatientList, epicOSWConfig, gx, useProviderInsuranceOnRegForm, insuranceOptions, productName, providerId, purpose, services, visibility, backToSearchPageURL, apiBaseURL }) => ({
    id: `Machine__${States_1.loadingProvider}`,
    initial: States_1.pending,
    context: {
        covidVaccinePatientRegFormConfig,
        patientRegFormConfig,
        customerCode,
        dbEnabledProperty,
        deepLinkingParams,
        ehr,
        ehrPatientList,
        epicOSWConfig,
        gx,
        useProviderInsuranceOnRegForm,
        insuranceOptions,
        productName,
        provider: null,
        providerId: providerId,
        purpose,
        services,
        visibility,
        backToSearchPageURL,
        apiBaseURL
    },
    states: {
        [States_1.idle]: {},
        [States_1.success]: {
            entry: 'sendParentNext'
        },
        [States_1.noResults]: {},
        [States_1.bookingDisabled]: {},
        [States_1.pending]: {
            invoke: {
                id: 'getData',
                src: 'getData',
                onDone: [
                    { cond: 'hasNoProviderResults', target: States_1.noResults },
                    { cond: 'isBookingDisabled', target: States_1.bookingDisabled },
                    {
                        cond: 'hasDeepLinkedPurposeAndShouldGetEhrPatientDemographics',
                        actions: [
                            'setResults',
                            // deepLinking does not contain visit_method, thus adding it here so we can later check for certain conditional GX questions (e.g. the telehealth attestation question)
                            'setApptPurposeVisitMethod',
                            'setConditionalGxContext'
                        ],
                        target: States_1.deepLinkedPurposeAndPendingEhrPatientDemographics
                    },
                    {
                        cond: 'shouldGetEhrPatientDemographics',
                        actions: 'setResults',
                        target: States_1.pendingEhrPatientDemographics
                    },
                    {
                        cond: 'hasDeepLinkedPurpose',
                        actions: [
                            'setResults',
                            // deepLinking does not contain visit_method, thus adding it here so we can later check for certain conditional GX questions (e.g. the telehealth attestation question)
                            'setApptPurposeVisitMethod',
                            'setConditionalGxContext'
                        ],
                        target: States_1.deepLinkedPurpose
                    },
                    {
                        actions: 'setResults',
                        target: States_1.success
                    }
                ],
                onError: States_1.noResults
            }
        },
        [States_1.deepLinkedPurposeAndPendingEhrPatientDemographics]: {
            exit: ['setConditionalContextualInfoSent', 'clearConditionalGxContext'],
            invoke: {
                id: 'getV2ConditionalsAndGetEhrPatientDemographics',
                src: 'getV2ConditionalsAndGetEhrPatientDemographics',
                onDone: [
                    {
                        cond: 'hasNoConditionalGxQuestions',
                        actions: 'setEhrPatientDemographics',
                        target: States_1.success
                    },
                    {
                        actions: ['setConditionalGxQuestions', 'setEhrPatientDemographics'],
                        target: States_1.success
                    }
                ],
                onError: States_1.success // In the case of an error from fetching ehr data, the user will be brought to blank form with error banner indicating load failure
            }
        },
        [States_1.pendingEhrPatientDemographics]: {
            invoke: {
                id: 'getEhrPatientDemographics',
                src: 'getEhrPatientDemographics',
                onDone: {
                    actions: 'setEhrPatientDemographics',
                    target: States_1.success
                },
                onError: States_1.success // User will be brought to blank form with error banner indicating load failure
            }
        },
        [States_1.deepLinkedPurpose]: {
            exit: ['setConditionalContextualInfoSent', 'clearConditionalGxContext'],
            invoke: {
                id: 'getV2Conditionals',
                src: 'getV2Conditionals',
                onDone: [
                    {
                        cond: 'hasNoConditionalGxQuestions',
                        target: States_1.success
                    },
                    {
                        actions: 'setConditionalGxQuestions',
                        target: States_1.success
                    }
                ],
                onError: States_1.noResults // during a deepLink, if the conditional GX call fails, we don't want the user to be able to book w/o answering those questions
            }
        }
    }
});
exports.machineJSON = machineJSON;
const createProviderMachine = (_a) => {
    var { config = (0, exports.makeConfig)() } = _a, props = __rest(_a, ["config"]);
    return (0, xstate_1.Machine)((0, exports.machineJSON)(props)).withConfig(config);
};
exports.createProviderMachine = createProviderMachine;
const makeConfig = () => ({ services, actions, guards });
exports.makeConfig = makeConfig;
//# sourceMappingURL=provider.js.map